import { useInvokeSWRService, useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { isEmpty } from '@dxb-web-ui-framework/common/utils';
import React, { FC } from 'react';
import { makeStyles } from '@dxb-web-ui-framework/design-system';
import { useTranslation } from 'react-i18next';
import { fundTransfersService } from '../../../../channels/secure-web/services';
import { ErrorContent } from '../../../content-composer/src/components/Partials/ErrorContent';
import { GenericDrawer } from '../../../content-composer/src/components/Generic/GenericDrawer';
import { ExchangeCalculateResponseBody } from '../../../fundTransfer/src/domain/Transfer/entities/ExchangeCalculateResponseDTO';
import { exchangeAmountPredicator } from '../../../transaction/src/utils';
import {
  useTransactionDetails,
  useTransactionsProviderErrorState,
  useTransactionsProviderLoadingState,
  useTransactionsProviderActions,
  useCheckAuthorization,
} from '../contexts/Transactions/hooks';
import { TransactionDetailsContent } from './TransactionsDetailsContent';

export interface TransactionsWithDealerRateDrawerProps {
  isOpen: boolean;
  onToggleDrawer: (orderId: number) => void;
}

const useStyles = makeStyles(theme => ({
  customDrawerBodyClass: {
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'inherit',
    },
  },
}));

const TransactionsWithDealerRateDrawer: FC<TransactionsWithDealerRateDrawerProps> = ({ isOpen, onToggleDrawer }) => {
  const { t } = useTranslation();
  const isLoadingAuthorization = useTransactionsProviderLoadingState('isLoadingCheckAuthorization');
  const isLoadingTransactionDetails = useTransactionsProviderLoadingState('isLoadingTransactionDetails');
  const transactionDetailsError = useTransactionsProviderErrorState('transactionDetailsError');
  const transactionDetails = useTransactionDetails();
  const checkAuthorization = useCheckAuthorization();
  const { onRefetchTransactionDetails } = useTransactionsProviderActions();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const classes = useStyles();

  //bank employer has accepted in core system the requested dealer rate
  const doesUserAwaitsForDealerRateAcceptance = Boolean(
    transactionDetails &&
      transactionDetails.processingStatus === '6' &&
      !transactionDetails.dealRefNo &&
      !transactionDetails.dealUsrCode,
  );

  const shouldMakeExchangeAmountCall =
    !!transactionDetails &&
    ((exchangeAmountPredicator(transactionDetails?.processingStatus) &&
      (transactionDetails?.fromCurrencyId !== transactionDetails?.toCurrencyId ||
        (transactionDetails?.internalDestinationCurrencyId !== 0 &&
          transactionDetails?.internalDestinationCurrencyId !== transactionDetails?.fromCurrencyId))) ||
      doesUserAwaitsForDealerRateAcceptance);

  const {
    data: exchangeCalculateData,
    error: exchangeCalculateError,
    isLoading: isLoadingExchangeCalculate,
  } = useInvokeSWRService<ExchangeCalculateResponseBody>(
    fundTransfersService.ExchangeCalculate,
    {
      fromCurrencyId: transactionDetails?.fromCurrencyId,
      toCurrencyId:
        transactionDetails?.internalDestinationCurrencyId !== 0 &&
        transactionDetails?.internalDestinationCurrencyId !== transactionDetails?.fromCurrencyId
          ? transactionDetails?.internalDestinationCurrencyId
          : transactionDetails?.toCurrencyId,
      amount: transactionDetails?.amount,
      isSideTypeBuy: transactionDetails?.accountCurrency === transactionDetails?.exchangeCurrency,
    },
    shouldMakeExchangeAmountCall,
    'fundTransfersService.ExchangeCalculate',
  );

  const error = transactionDetailsError || exchangeCalculateError;
  const isLoading = isLoadingTransactionDetails || isLoadingExchangeCalculate || isLoadingAuthorization;

  return (
    <GenericDrawer
      anchor='bottom'
      title={t('transactionDetailsDrawer.Title')}
      isOpen={isOpen}
      stickDrawerTitleToLeft={isMobile}
      disableMobileDrawerHeight={isMobile}
      onToggleDrawer={onToggleDrawer}
      customDrawerBodyClass={classes.customDrawerBodyClass}>
      {!isEmpty(error) && (
        <ErrorContent
          code={error?.code || '404'}
          onClickBtn={onRefetchTransactionDetails}
          subtitle={error?.message}
          btnLabel={t('authorizationsListPage.Transactions.ErrorContent.BtnLabel')}
          message={t('authorizationsListPage.Transactions.ErrorContent.Message')}
        />
      )}
      {isEmpty(error) && (
        <TransactionDetailsContent
          lastAuthorizer={checkAuthorization?.lastAuthorizer ?? false}
          needMandate={checkAuthorization?.needMandate ?? true}
          doesUserAwaitsForDealerRateAcceptance={doesUserAwaitsForDealerRateAcceptance}
          isLoading={isLoading}
          transactionDetails={transactionDetails}
          exchangeCalculateData={exchangeCalculateData}
        />
      )}
    </GenericDrawer>
  );
};

export { TransactionsWithDealerRateDrawer };
